
import React from "react";
import { Input } from "@/components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, X } from "lucide-react";
import { ProfileFilter } from "@/hooks/useCommunityProfiles";

interface ProfileFiltersProps {
  filters: ProfileFilter;
  setFilters: (filters: ProfileFilter) => void;
  filterOptions: {
    countries: string[];
    universities: string[];
    expertiseAreas: string[];
  };
}

const ProfileFilters: React.FC<ProfileFiltersProps> = ({
  filters,
  setFilters,
  filterOptions
}) => {
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, searchTerm: e.target.value });
  };

  const handleFilterChange = (key: keyof Omit<ProfileFilter, 'searchTerm'>, value: string | null) => {
    setFilters({ ...filters, [key]: value });
  };
  
  const resetFilters = () => {
    setFilters({
      searchTerm: "",
      country: null,
      university: null,
      expertise: null
    });
  };
  
  const hasActiveFilters = 
    !!filters.searchTerm || 
    !!filters.country || 
    !!filters.university || 
    !!filters.expertise;

  return (
    <div className="bg-card border rounded-lg p-4 mb-6 space-y-4">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, lab, university, expertise..."
          value={filters.searchTerm}
          onChange={handleSearchChange}
          className="pl-9"
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Select
          value={filters.country || undefined}
          onValueChange={(value) => handleFilterChange("country", value || null)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Filter by country" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all-countries">All Countries</SelectItem>
            {filterOptions.countries.map((country) => (
              <SelectItem key={country} value={country}>
                {country}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select
          value={filters.university || undefined}
          onValueChange={(value) => handleFilterChange("university", value || null)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Filter by university" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all-universities">All Universities</SelectItem>
            {filterOptions.universities.map((university) => (
              <SelectItem key={university} value={university}>
                {university}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select
          value={filters.expertise || undefined}
          onValueChange={(value) => handleFilterChange("expertise", value || null)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Filter by expertise" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all-expertise">All Expertise</SelectItem>
            {filterOptions.expertiseAreas.map((expertise) => (
              <SelectItem key={expertise} value={expertise}>
                {expertise}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {hasActiveFilters && (
        <div className="flex justify-end">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={resetFilters}
            className="flex items-center"
          >
            <X className="h-4 w-4 mr-1" />
            Clear Filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProfileFilters;
